// src/pages/AgendaForm.js
import React, { useState } from "react";
import { useHistory } from "react-router-dom"; // react-router-dom v5
import { apiFetch } from "../utils/wkCommon";   // pastikan path sesuai struktur project
import { getJwt, getUser } from "../utils/auth";

const API_URL = process.env.REACT_APP_BACKEND_URL || ""; // jangan hardcode lagi

function nowIso() {
  return new Date().toISOString();
}

/**
 * Simple debug entry structure:
 * { id, when, type: "request"|"response"|"error", method, url, headers, body, status, raw }
 */
export default function AgendaForm() {
  const history = useHistory();

  const [subject, setSubject] = useState("");
  const [agendaDate, setAgendaDate] = useState(""); // yyyy-mm-dd
  const [updateId, setUpdateId] = useState(""); // id untuk PUT (kosong = hanya POST)
  const [loading, setLoading] = useState(false);

  const [debug, setDebug] = useState([]);
  const [showDebug, setShowDebug] = useState(true);

  function pushDebug(entry) {
    setDebug((d) => [entry, ...d].slice(0, 200)); // simpan paling baru di atas, batasi 200 entri
  }

  function clearDebug() {
    setDebug([]);
  }

  // Helper untuk build headers (untuk debug visibility). apiFetch sudah menambahkan Authorization.
  function buildHeaders() {
    const jwt = getJwt();
    const base = { "Content-Type": "application/json; charset=UTF-8" };
    if (jwt) base.Authorization = `Bearer ${jwt}`;
    return base;
  }

  // Ambil identitas user dari JWT (sinkron). getUser() sesuai instruksi project.
  function resolveActor() {
    try {
      const u = getUser();
      // prioritas: kode umat (profil) -> username -> email -> 'unknown'
      const actor =
        (u && u.umat && u.umat.kode) ||
        (u && (u.username || u.email)) ||
        "unknown";
      return actor;
    } catch (e) {
      return "unknown";
    }
  }

  async function handleCreate(e) {
    e && e.preventDefault();
    setLoading(true);
    const urlPath = "/api/agendas";
    const urlFull = `${API_URL.replace(/\/$/, "")}${urlPath}`;
    const method = "POST";
    //const payload = { data: { subject: subject || "", agendaDate: agendaDate || null } };

    // set createdBy & updatedBy from JWT (via getUser())
    const actor = resolveActor();
    const payload = {
      data: {
        subject: subject || "",
        agendaDate: agendaDate || null,
        createdBy: actor,
        updatedBy: actor,
      },
    };

    // debug: request
    pushDebug({
      id: Math.random().toString(36).slice(2, 9),
      when: nowIso(),
      type: "request",
      method,
      url: urlFull,
      headers: buildHeaders(),
      body: payload,
    });

    try {
      const resp = await apiFetch(urlPath, { method, body: payload });
      pushDebug({
        id: Math.random().toString(36).slice(2, 9),
        when: nowIso(),
        type: "response",
        method,
        url: urlFull,
        status: resp?.status ?? "200/OK",
        raw: resp,
      });
      setLoading(false);
      // tampilkan sukses singkat dan kosongkan form (atau setUpdateId untuk edit)
      alert("Agenda created successfully (see debug).");
      setSubject("");
      setAgendaDate("");
      setUpdateId(resp?.data?.id ? String(resp.data.id) : "");
    } catch (err) {
      setLoading(false);
      // err mungkin Error dengan message
      pushDebug({
        id: Math.random().toString(36).slice(2, 9),
        when: nowIso(),
        type: "error",
        method,
        url: urlFull,
        raw: (err && err.message) || err,
      });
      console.error("create error", err);
      alert("Create failed: " + (err?.message ?? err));
    }
  }

  async function handleUpdate(e) {
    e && e.preventDefault();
    if (!updateId) {
      alert("Masukkan ID agenda di field 'ID (untuk update)'.");
      return;
    }
    setLoading(true);
    const urlPath = `/api/agendas/${encodeURIComponent(updateId)}`;
    const urlFull = `${API_URL.replace(/\/$/, "")}${urlPath}`;
    const method = "PUT";
    //const payload = { data: { subject: subject || "", agendaDate: agendaDate || null } };

    const actor = resolveActor();
    // hanya updatedBy perlu di-update
    const payload = {
      data: {
        subject: subject || "",
        agendaDate: agendaDate || null,
        updatedBy: actor,
      },
    };

    pushDebug({
      id: Math.random().toString(36).slice(2, 9),
      when: nowIso(),
      type: "request",
      method,
      url: urlFull,
      headers: buildHeaders(),
      body: payload,
    });

    try {
      const resp = await apiFetch(urlPath, { method, body: payload });
      pushDebug({
        id: Math.random().toString(36).slice(2, 9),
        when: nowIso(),
        type: "response",
        method,
        url: urlFull,
        status: resp?.status ?? "200/OK",
        raw: resp,
      });
      setLoading(false);
      alert("Agenda updated successfully (see debug).");
    } catch (err) {
      setLoading(false);
      pushDebug({
        id: Math.random().toString(36).slice(2, 9),
        when: nowIso(),
        type: "error",
        method,
        url: urlFull,
        raw: (err && err.message) || err,
      });
      console.error("update error", err);
      alert("Update failed: " + (err?.message ?? err));
    }
  }

  // optional: quick fetch to populate form by id
  async function handleLoadById(e) {
    e && e.preventDefault();
    if (!updateId) {
      alert("Masukkan ID agenda untuk memuat.");
      return;
    }
    const urlPath = `/api/agendas/${encodeURIComponent(updateId)}?populate=*`;
    const urlFull = `${API_URL.replace(/\/$/, "")}${urlPath}`;
    pushDebug({
      id: Math.random().toString(36).slice(2, 9),
      when: nowIso(),
      type: "request",
      method: "GET",
      url: urlFull,
      headers: buildHeaders(),
    });

    try {
      const resp = await apiFetch(urlPath);
      pushDebug({
        id: Math.random().toString(36).slice(2, 9),
        when: nowIso(),
        type: "response",
        method: "GET",
        url: urlFull,
        raw: resp,
      });

      // Strapi returns data structure: resp.data.attributes (v4/v5 differences possible)
      const item = resp?.data ?? resp;
      const attrs = item?.attributes ?? item;
      setSubject(attrs?.subject ?? "");
      // agendaDate may include time; take YYYY-MM-DD
      const d = attrs?.agendaDate ?? attrs?.agenda_date ?? "";
      setAgendaDate(d ? String(d).slice(0, 10) : "");
      alert("Loaded into form (see debug).");
    } catch (err) {
      pushDebug({
        id: Math.random().toString(36).slice(2, 9),
        when: nowIso(),
        type: "error",
        method: "GET",
        url: urlFull,
        raw: (err && err.message) || err,
      });
      console.error("load error", err);
      alert("Load failed: " + (err?.message ?? err));
    }
  }

  return (
    <div style={{ padding: 20, fontFamily: "Inter, Roboto, sans-serif" }}>
      <h2>Agenda — Create / Update (REST)</h2>
      <p style={{ marginTop: 0, color: "#666" }}>
        Backend: <code>{API_URL}</code>
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleCreate();
        }}
        style={{
          display: "grid",
          gap: 12,
          maxWidth: 720,
          background: "#fff",
          padding: 16,
          borderRadius: 8,
          boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
        }}
      >
        <label>
          ID (untuk update / load):
          <input
            value={updateId}
            onChange={(e) => setUpdateId(e.target.value)}
            placeholder="kosong = create"
            style={{ width: "100%", padding: "8px 10px", marginTop: 6 }}
          />
        </label>

        <label>
          Subject:
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Judul agenda..."
            style={{ width: "100%", padding: "8px 10px", marginTop: 6 }}
          />
        </label>

        <label>
          Agenda Date:
          <input
            type="date"
            value={agendaDate}
            onChange={(e) => setAgendaDate(e.target.value)}
            style={{ width: "fit-content", padding: "8px 10px", marginTop: 6 }}
          />
        </label>

        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button
            type="button"
            onClick={handleCreate}
            disabled={loading}
            style={{ padding: "8px 12px" }}
          >
            {loading ? "Processing..." : "Create (POST)"}
          </button>

          <button
            type="button"
            onClick={handleUpdate}
            disabled={loading || !updateId}
            style={{ padding: "8px 12px" }}
          >
            {loading ? "Processing..." : "Update (PUT)"}
          </button>

          <button
            type="button"
            onClick={handleLoadById}
            disabled={!updateId}
            style={{ padding: "8px 12px" }}
          >
            Load by ID
          </button>

          <button
            type="button"
            onClick={() => {
              setSubject("");
              setAgendaDate("");
              setUpdateId("");
            }}
            style={{ padding: "8px 12px" }}
          >
            Reset
          </button>

          <button
            type="button"
            onClick={() => {
              setShowDebug((s) => !s);
            }}
            style={{ marginLeft: "auto", padding: "8px 12px" }}
          >
            {showDebug ? "Hide Debug" : "Show Debug"}
          </button>
        </div>
      </form>

      {/* Debug panel */}
      {showDebug && (
        <div
          style={{
            marginTop: 20,
            maxWidth: 1000,
            background: "#0f172a",
            color: "#e6eef8",
            borderRadius: 8,
            padding: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h3 style={{ margin: 0 }}>Debug Trace</h3>
            <small style={{ color: "#9fb0d9" }}>
              entries: <strong>{debug.length}</strong>
            </small>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button
                onClick={() => {
                  navigator.clipboard
                    .writeText(JSON.stringify(debug, null, 2))
                    .then(() => alert("Debug copied to clipboard"));
                }}
              >
                Copy JSON
              </button>
              <button
                onClick={() => {
                  clearDebug();
                }}
              >
                Clear
              </button>
            </div>
          </div>

          <div style={{ marginTop: 12, maxHeight: 420, overflow: "auto" }}>
            {debug.length === 0 && (
              <div style={{ color: "#9fb0d9" }}>No debug entries yet.</div>
            )}
            {debug.map((d) => (
              <div
                key={d.id}
                style={{
                  borderLeft:
                    d.type === "error"
                      ? "4px solid #ff7878"
                      : d.type === "request"
                      ? "4px solid #7cc7ff"
                      : "4px solid #92f78e",
                  padding: 10,
                  marginBottom: 10,
                }}
              >
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <div style={{ minWidth: 120, color: "#cfe8ff" }}>
                    [{d.type.toUpperCase()}] {d.when}
                  </div>
                  <div style={{ color: "#bfe0ff" }}>
                    {d.method || "GET"} {d.url ? d.url.replace(API_URL, "") : ""}
                  </div>
                  <div style={{ marginLeft: "auto", color: "#9fb0d9" }}>
                    {d.status ? `status: ${d.status}` : null}
                  </div>
                </div>

                {d.headers && (
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: "pointer" }}>Headers</summary>
                    <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
                      {JSON.stringify(d.headers, null, 2)}
                    </pre>
                  </details>
                )}

                {d.body && (
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: "pointer" }}>Request Body</summary>
                    <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
                      {JSON.stringify(d.body, null, 2)}
                    </pre>
                  </details>
                )}

                {d.raw && (
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: "pointer" }}>
                      Response / Error (raw)
                    </summary>
                    <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
                      {typeof d.raw === "string"
                        ? d.raw
                        : JSON.stringify(d.raw, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
